import { TranslationKey } from '@Localization';
import { inject, injectable, interfaces } from 'inversify';
import { TimePeriod } from '../../../design/selects/timePeriodSelector/S_TimePeriodSelector';
import { ICurrentWorkspaceStore } from '../../../modules/workspaces/currentWorkspace/CurrentWorkspace.store';
import {
  CurrentWorkspaceChangedEvent,
  CurrentWorkspaceChangedEventType,
} from '../../../modules/workspaces/currentWorkspace/events/CurrentWorkspaceChangedEvent';
import { ACDLError, ForbiddenError } from '../../models/error.model';
import { SortingDirection } from '../../models/sortingDirection.model';
import { EventListeningDisposer, IEventBus } from '../../services/eventBus.service';
import { NotificationLevel, ToastNotification } from '../notifications/models/notification.model';
import { INotificationsService } from '../notifications/services/notifications.service';
import { WorkspaceRole } from '../workspaces/workspaces.store';
import { IGetTeamMembersService } from './services/getTeamMembers.service';
import { ITeamApiService } from './services/teamApi.service';
import { ITeamStore } from './team.store';

export interface ITeamService {
  initializeAsync(): Promise<void>;
  dispose(): void;
  changeStatisticsTimePeriodAsync(timePeriod: TimePeriod): Promise<void>;
  refreshStatisticsAsync(): Promise<void>;
  changeSorting(orderBy: string, orderDirection: SortingDirection): Promise<void>;
  addUserToWorkspaceAsync(userEmail: string, role: WorkspaceRole): Promise<void | ACDLError>;
  changeUserRoleAsync(userEmail: string, role: WorkspaceRole): Promise<void | ACDLError>;
  removeUserFromWorkspaceAsync(userEmail: string): Promise<void | ACDLError>;
  changePagination(pageNumber: number, pageSize: number): Promise<void>;
}

export namespace ITeamService {
  export const $: interfaces.ServiceIdentifier<ITeamService> = Symbol('TEAM_SERVICE');
}

@injectable()
export class TeamService implements ITeamService {
  private workspaceChangedListenerDisposer?: EventListeningDisposer;

  constructor(
    @inject(ITeamStore.$) private readonly teamStore: ITeamStore,
    @inject(ITeamApiService.$) private readonly teamApiService: ITeamApiService,
    @inject(IEventBus.$) private readonly eventBus: IEventBus,
    @inject(INotificationsService.$) private readonly notificationsService: INotificationsService,
    @inject(IGetTeamMembersService.$) private readonly getWorkspaceTeamMembersService: IGetTeamMembersService,
    @inject(ICurrentWorkspaceStore.$) private readonly currentWorkspaceStore: ICurrentWorkspaceStore
  ) {}

  async initializeAsync(): Promise<void> {
    this.workspaceChangedListenerDisposer = this.eventBus.addListener(this.currentWorkspaceChangedListener, CurrentWorkspaceChangedEventType);
    await this.getTeamMembersAsync();
  }

  dispose(): void {
    this.workspaceChangedListenerDisposer?.();
  }

  public async addUserToWorkspaceAsync(userEmail: string, role: WorkspaceRole): Promise<ACDLError | undefined> {
    const result = await this.teamApiService.addUserToWorkspaceAsync({
      userEmail,
      role,
      workspaceId: this.currentWorkspaceStore.currentWorkspace.id,
    });

    if (result instanceof ACDLError) {
      await this.notifyAboutApiError(result, 'adding_user_to_workspace_failed');
      return result;
    }

    await this.getTeamMembersAsync();
    return Promise.resolve(undefined);
  }

  public async changeUserRoleAsync(userEmail: string, role: WorkspaceRole): Promise<ACDLError | undefined> {
    const result = await this.teamApiService.changeUserRoleAsync({
      userEmail,
      role,
      workspaceId: this.currentWorkspaceStore.currentWorkspace.id,
    });

    if (result instanceof ACDLError) {
      await this.notifyAboutApiError(result, 'role_change_failed', true);
      return result;
    }

    await this.getTeamMembersAsync();
    return Promise.resolve(undefined);
  }

  public async removeUserFromWorkspaceAsync(userEmail: string): Promise<ACDLError | undefined> {
    const result = await this.teamApiService.removeUserFromWorkspaceAsync({ userEmail, workspaceId: this.currentWorkspaceStore.currentWorkspace.id });

    if (result instanceof ACDLError) {
      await this.notifyAboutApiError(result, 'removing_user_from_workspace_failed');
      return result;
    }

    await this.getTeamMembersAsync();
    return Promise.resolve(undefined);
  }

  public async changeStatisticsTimePeriodAsync(timePeriod: TimePeriod): Promise<void> {
    if (this.teamStore.statisticsTimePeriod !== timePeriod) {
      this.teamStore.setStatisticsTimePeriod(timePeriod);
      await this.getTeamMembersAsync();
    }
  }

  public async refreshStatisticsAsync(): Promise<void> {
    await this.getTeamMembersAsync();
  }

  public async changeSorting(orderBy: string, orderDirection: SortingDirection): Promise<void> {
    this.teamStore.setSorting(orderBy, orderDirection);
    await this.getTeamMembersAsync();
  }

  public async getTeamMembersAsync(): Promise<void> {
    this.teamStore.setIsLoading(true);

    try {
      const { pageNumber, pageSize } = this.teamStore.pagination;
      const { orderBy, orderDirection } = this.teamStore;
      const workspaceId = this.currentWorkspaceStore.currentWorkspace.id;
      const { statisticsTimePeriod } = this.teamStore;

      const request = {
        workspaceId,
        timePeriod: statisticsTimePeriod,
        pageNumber,
        pageSize,
        orderBy,
        orderType: orderDirection,
      };

      const result = await this.getWorkspaceTeamMembersService.getTeamMembersAsync(request);
      this.teamStore.setTeamMembers(result.data);
    } catch (error) {
      await this.notifyAboutApiError(error, 'fetching_team_failed');
    }

    this.teamStore.setIsLoading(false);
  }

  private async notifyAboutApiError(result: unknown, errorCode: TranslationKey, dontShowBadRequest: boolean = false): Promise<void> {
    if (result instanceof ACDLError && !(result instanceof ForbiddenError) && (!dontShowBadRequest || !result.isBadRequest())) {
      await this.notificationsService.push(new ToastNotification(NotificationLevel.ERROR, errorCode));
    }
  }

  async changePagination(pageNumber: number, pageSize: number): Promise<void> {
    this.teamStore.setPagination(pageNumber, pageSize);
    await this.getTeamMembersAsync();
  }

  currentWorkspaceChangedListener = async (event: CurrentWorkspaceChangedEvent): Promise<void> => {
    if (!event.willBeNavigatedToDifferentPath) {
      await this.getTeamMembersAsync();
    }
  };
}
